import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { placeCreatedRowFirst } from "utils/helpers";
import { ListPaginationMeta } from "utils/helpers/pagination";

import {
  contactForm,
  Customer,
  CustomerContactPerson,
  CustomerContactUpdate,
  CustomerDetails,
  CustomerDocument,
  CustomerForm,
  CustomerPriceQuote,
  CustomerStatusFilter,
  CustomerUpdate,
  DocumentUploadFile,
} from "../types";
import {
  emptyCustomerDetails,
  initialContactForm,
  initialCustomerForm,
  initialState,
} from "./initialState";

export const sliceKey = "customersState";

const customersSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getCustomersList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getCustomersListSuccess(
      state,
      action: PayloadAction<{
        data: Customer[];
        meta: ListPaginationMeta;
      }>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getCustomersListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (
        state.loading ||
        state.meta.currentPage >= state.meta.totalPages
      ) {
        return;
      }

      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<{
        data: Customer[];
        meta: ListPaginationMeta;
      }>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setCustomersStatusFilter(
      state,
      action: PayloadAction<CustomerStatusFilter>
    ) {
      state.statusFilter = action.payload;
    },

    getCustomerDetails(state, action: PayloadAction<string>) {
      state.detailLoading = true;
      state.error = null;
      state.selectedCustomerId = action.payload;
      if (state.customerDetails?.customerId !== Number(action.payload)) {
        state.customerDetails = null;
      }
    },

    getCustomerDetailsSuccess(state, action: PayloadAction<CustomerDetails>) {
      state.detailLoading = false;
      state.customerDetails = action.payload;
      state.error = null;
    },

    getCustomerDetailsError(state, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.customerDetails = {
        ...emptyCustomerDetails,
        customerId: Number(state.selectedCustomerId),
      };
      state.error = action.payload;
    },

    setCustomerDetailsStatus(state, action: PayloadAction<string>) {
      if (!state.customerDetails) {
        return;
      }
      state.customerDetails.status = action.payload;
    },

    clearCustomerDetails(state) {
      state.selectedCustomerId = null;
      state.customerDetails = null;
      state.detailLoading = false;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    ContactModalOpen(state) {
      state.isAddContactModalOpen = true;
      state.error = null;
    },

    openAddDocumentModal(state) {
      state.isAddDocumentModalOpen = true;
      state.documentUploadFile = null;
      state.documentUploadError = "";
      state.error = null;
    },

    closeAddDocumentModal(state) {
      state.isAddDocumentModalOpen = false;
      state.documentUploadFile = null;
      state.documentUploadError = "";
      state.error = null;
    },

    setDocumentUploadFile(state, action: PayloadAction<DocumentUploadFile | null>) {
      state.documentUploadFile = action.payload;
      state.documentUploadError = "";
    },

    setDocumentUploadError(state, action: PayloadAction<string>) {
      state.documentUploadError = action.payload;
    },

    resetAddDocumentForm(state) {
      state.documentUploadFile = null;
      state.documentUploadError = "";
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialCustomerForm;
    },

    closeAddContactModal(state) {
      state.isAddContactModalOpen = false;
      state.contactSaving = false;
      state.error = null;
      state.contactForm = initialContactForm;
    },
    setFormField(
      state,
      action: PayloadAction<{
        name: keyof CustomerForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    setContactFormField(
      state,
      action: PayloadAction<{
        name: keyof contactForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.contactForm[name] = value;
    },

    addCustomer(state) {
      state.saving = true;
      state.error = null;
    },

    

    addCustomerSuccess(state, action: PayloadAction<Customer>) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialCustomerForm;
      state.error = null;
      state.list = placeCreatedRowFirst(state.list, action.payload);
    },

    addCustomerError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateCustomer(state, _action: PayloadAction<CustomerUpdate>) {
      state.saving = true;
      state.error = null;
    },

    updateCustomerSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateCustomerError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateCustomerContact(
      state,
      _action: PayloadAction<CustomerContactUpdate>
    ) {
      state.saving = true;
      state.error = null;
    },

    updateCustomerContactSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateCustomerContactError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    addCustomerContact(state) {
      state.contactSaving = true;
      state.error = null;
    },

    addCustomerContactSuccess(state, action: PayloadAction<CustomerContactPerson>) {
      state.contactSaving = false;
      state.error = null;
      state.customerDetails?.contacts.push(action.payload);
    },

    addCustomerContactError(state, action: PayloadAction<string>) {
      state.contactSaving = false;
      state.error = action.payload;
    },

    deleteCustomer(state, _action: PayloadAction<number>) {
      state.saving = true;
      state.error = null;
    },

    deleteCustomerSuccess(state, action: PayloadAction<number>) {
      const id = action.payload;
      state.saving = false;
      state.error = null;

      state.list = state.list.filter(
        (customer) => customer.customerId !== id
      );

      if (state.selectedCustomerId === String(id)) {
        state.selectedCustomerId = null;
        state.customerDetails = null;
        state.detailLoading = false;
      }
    },

    deleteCustomerError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    deleteCustomerContact(state, _action: PayloadAction<number>) {
      state.saving = true;
      state.error = null;
    },

    deleteCustomerContactSuccess(state, action: PayloadAction<number>) {
      const contactId = action.payload;
      state.saving = false;
      state.error = null;

      if (state.customerDetails?.contacts) {
        state.customerDetails.contacts = state.customerDetails.contacts.filter(
          (contact) => contact.contactId !== contactId,
        );
      }
    },

    deleteCustomerContactError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    addCustomerPriceQuote(state, action: PayloadAction<CustomerPriceQuote>) {
      if (!state.customerDetails) {
        return;
      }
      state.customerDetails.priceQuotes = [
        action.payload,
        ...(state.customerDetails.priceQuotes || []),
      ];
    },

    addCustomerDocument(state, action: PayloadAction<CustomerDocument>) {
      if (!state.customerDetails) {
        return;
      }
      state.customerDetails.documents = [
        action.payload,
        ...(state.customerDetails.documents || []),
      ];
      state.isAddDocumentModalOpen = false;
      state.documentUploadFile = null;
      state.documentUploadError = "";
    },
  },
});

export const { actions: customersActions, reducer } = customersSlice;
